import React, { FC, useEffect, useState } from "react"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Slider from "@mui/material/Slider"
import Typography from "@mui/material/Typography"
import SectionWrapper from "./LockedSectionWrapper"
import VideoPlayer from "../../player/VideoPlayer"
import { Participant, Video } from "../../types"

// Where the 360 camera points up or down when the video opens. Gavin, 3 Nov:
// "Head is low — should be centre". The player opens level, at the horizon,
// so a face filmed below the camera's lens sits low; this tilts the starting
// view to it. Rotation adjustment turns the picture left/right; this is its
// up/down partner, but it tilts the camera rather than the picture, because
// a tilted picture would tip the horizon as soon as a viewer panned round.
//
// Viewers can still drag anywhere from there. Presentation, outside the
// manifest, so it stays editable once sealed. Letting go of the slider saves;
// the preview follows it as it moves.

// The player's normal view stops at 45° up or down (VideoPlayer360Controller
// polar limits), and so does the API (data.StartTiltLimit).
const LIMIT = 45

const MARKS = [-45, -30, -15, 0, 15, 30, 45].map((value) => ({ value, label: `${value}°` }))

interface StartTiltSectionProps {
  video: Video
  participants?: Participant[]
  rotationAdjustment?: number
  onSave: (degrees: number) => void
}

const StartTiltSection: FC<StartTiltSectionProps> = ({ video, participants, rotationAdjustment, onSave }) => {
  const stored = video.start_tilt ?? 0
  const [tilt, setTilt] = useState(stored)

  // Follow the server when the stored value changes under us.
  useEffect(() => { setTilt(stored) }, [stored])

  return (
    <SectionWrapper
      title="Starting tilt"
      status="pending"
      actions={
        <Button
          size="small"
          variant="outlined"
          disabled={stored === 0}
          onClick={() => {
            setTilt(0)
            onSave(0)
          }}
        >
          Reset to level
        </Button>
      }
    >
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        Where the camera points up or down when the video opens: 0° is the horizon, negative looks down.
        Use it to centre people filmed below the camera. Viewers can still look anywhere from there.
        Applies to every viewer; can be changed after sealing.
      </Typography>

      <Box sx={{ px: 1 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>Tilt (down ← → up)</Typography>
        <Slider
          value={tilt}
          min={-LIMIT}
          max={LIMIT}
          step={1}
          marks={MARKS}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v}°`}
          onChange={(_, v) => setTilt(v as number)}
          onChangeCommitted={(_, v) => onSave(v as number)}
        />
      </Box>

      {video.stream_url && (
        <Box sx={{ mt: 1 }}>
          <VideoPlayer
            src={video.stream_url}
            type="application/x-mpegURL"
            participants={participants || []}
            rotationAdjustment={rotationAdjustment}
            startTilt={tilt}
            compact
          />
        </Box>
      )}
    </SectionWrapper>
  )
}

export default StartTiltSection
